import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { User } from "@shared/schema";
import { useAuth } from "@/contexts/AuthContext";

// ── Step 1: Username ──────────────────────────────────────────────
const usernameSchema = z.object({
  username: z
    .string()
    .min(3, "Username must be at least 3 characters")
    .max(20, "Username must be at most 20 characters")
    .regex(/^[a-zA-Z0-9_]+$/, "Username can only contain letters, numbers, and underscores"),
});
type UsernameFormValues = z.infer<typeof usernameSchema>;

// ── Step 2: Parent info ───────────────────────────────────────────
const parentSchema = z.object({
  parentFirstName: z.string().min(1, "First name is required"),
  parentLastName: z.string().min(1, "Last name is required"),
  sameEmail: z.boolean(),
  parentEmail: z.string().email("Enter a valid email address").or(z.literal("")),
}).refine(
  (data) => data.sameEmail || data.parentEmail.length > 0,
  { message: "Parent email is required", path: ["parentEmail"] }
);
type ParentFormValues = z.infer<typeof parentSchema>;

interface UsernameSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

export function UsernameSelectionModal({ open, onClose }: UsernameSelectionModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();

  const needsUsername = !user?.username;
  const needsParent = !user?.parentFirstName || !user?.parentLastName || !user?.parentEmail;

  const [step, setStep] = useState<'username' | 'parent'>(
    needsUsername ? 'username' : 'parent'
  );

  // ── Username form ────────────────────────────────────────────────
  const usernameForm = useForm<UsernameFormValues>({
    resolver: zodResolver(usernameSchema),
    defaultValues: { username: "" },
  });

  const usernameMutation = useMutation({
    mutationFn: async (username: string) => {
      const response = await apiRequest("POST", "/api/auth/update-username", { username });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Failed to set username" }));
        throw new Error(err.error || "Failed to set username");
      }
      return response.json();
    },
    onSuccess: async (data: { user: User }) => {
      if (!data.user.username) throw new Error("Username was not saved. Please try again.");
      queryClient.setQueryData(["/api/auth/me"], data);
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      if (needsParent) {
        setStep('parent');
      } else {
        toast({ title: "All set!", description: "Your profile is complete." });
        onClose();
      }
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Failed to set username", description: error.message });
    },
  });

  // ── Parent form ──────────────────────────────────────────────────
  const parentForm = useForm<ParentFormValues>({
    resolver: zodResolver(parentSchema),
    defaultValues: {
      parentFirstName: user?.parentFirstName ?? "",
      parentLastName: user?.parentLastName ?? "",
      sameEmail: false,
      parentEmail: user?.parentEmail ?? "",
    },
  });

  const sameEmail = parentForm.watch("sameEmail");

  const parentMutation = useMutation({
    mutationFn: async (values: ParentFormValues) => {
      const parentEmail = values.sameEmail ? (user?.email ?? "") : values.parentEmail;
      const response = await apiRequest("PUT", "/api/profile/update", {
        parentFirstName: values.parentFirstName,
        parentLastName: values.parentLastName,
        parentEmail,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: "Failed to save parent info" }));
        throw new Error(err.error || "Failed to save parent info");
      }
      return response.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      toast({ title: "All set!", description: "Your profile is complete." });
      onClose();
    },
    onError: (error: Error) => {
      toast({ variant: "destructive", title: "Failed to save parent info", description: error.message });
    },
  });

  return (
    <Dialog open={open} modal>
      <DialogContent
        className="sm:max-w-[440px]"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Progress bar: only shown when both steps are needed */}
        {needsUsername && needsParent && (
          <div className="flex items-center gap-2 mb-1">
            <div className="h-1.5 flex-1 rounded-full bg-[#0789c3]" />
            <div className={`h-1.5 flex-1 rounded-full ${step === 'parent' ? 'bg-[#0789c3]' : 'bg-gray-200'}`} />
          </div>
        )}

        {step === 'username' && (
          <>
            <DialogHeader>
              <DialogTitle>Choose Your Username</DialogTitle>
              <DialogDescription>
                Pick a unique username that other students will see on leaderboards and in messages.
              </DialogDescription>
            </DialogHeader>

            <Form {...usernameForm}>
              <form onSubmit={usernameForm.handleSubmit((d) => usernameMutation.mutate(d.username))} className="space-y-4">
                <FormField
                  control={usernameForm.control}
                  name="username"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Username</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., superstudent123" {...field} data-testid="input-username" />
                      </FormControl>
                      <FormDescription>3–20 characters, letters, numbers, and underscores only</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={usernameMutation.isPending}
                    className="bg-[#0789c3] hover:bg-[#0678ab]"
                    data-testid="button-submit-username"
                  >
                    {usernameMutation.isPending ? "Saving…" : needsParent ? "Next →" : "Done"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}

        {step === 'parent' && (
          <>
            <DialogHeader>
              <DialogTitle>Parent / Guardian Details</DialogTitle>
              <DialogDescription>
                We use this to keep parents updated on their child's progress and to send important account notices.
              </DialogDescription>
            </DialogHeader>

            <Form {...parentForm}>
              <form onSubmit={parentForm.handleSubmit((d) => parentMutation.mutate(d))} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={parentForm.control}
                    name="parentFirstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Jane" {...field} data-testid="input-parent-first-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={parentForm.control}
                    name="parentLastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} data-testid="input-parent-last-name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={parentForm.control}
                  name="sameEmail"
                  render={({ field }) => (
                    <FormItem className="flex items-start gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            if (checked) parentForm.setValue("parentEmail", "", { shouldValidate: true });
                          }}
                          data-testid="checkbox-same-email"
                        />
                      </FormControl>
                      <FormLabel className="font-normal cursor-pointer leading-snug">
                        Parent's email is the same as the account email
                        {user?.email && <span className="text-muted-foreground ml-1">({user.email})</span>}
                      </FormLabel>
                    </FormItem>
                  )}
                />

                {!sameEmail && (
                  <FormField
                    control={parentForm.control}
                    name="parentEmail"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Parent's Email Address</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="parent@example.com" {...field} data-testid="input-parent-email" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={parentMutation.isPending}
                    className="bg-[#0789c3] hover:bg-[#0678ab]"
                    data-testid="button-submit-parent"
                  >
                    {parentMutation.isPending ? "Saving…" : "Complete Setup"}
                  </Button>
                </div>
              </form>
            </Form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
